import { useCallback, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { useBatchComponentUpdateContext } from '../shared/batch-component-update.context';
import {
  INFO_BOX_COLOURS,
  INFO_BOX_TYPENAME,
  InfoBoxProperties,
  SettingsProps,
} from './info-box.constants';
import {
  SettingsGroup,
  SettingsLabel,
} from 'approot/activity-builder/settings/settings.style';
import {
  ARROW_ALIGNMENT_BOTTOM,
  ARROW_ALIGNMENT_CENTRE,
  ARROW_ALIGNMENT_LEFT,
  ARROW_ALIGNMENT_RIGHT,
  ARROW_ALIGNMENT_TOP,
  POSITION_BOTTOM,
  POSITION_LEFT,
  POSITION_RIGHT,
  POSITION_TOP,
  ArrowAlignment,
  BORDER_STYLE_BOX,
  BORDER_STYLE_INSTRUCTION,
  BORDER_STYLE_NONE,
  BORDER_STYLE_SPEECH,
  BORDER_STYLE_STATEMENT,
  BorderStyle,
  FLEX_ALIGN_START,
} from 'global/constants';
import {
  ActivityBuilderDrawerContent,
  ActivityBuilderDrawerHeader,
  ActivityBuilderDrawerWrapper,
} from 'approot/activity-builder/activity-builder-drawer';
import { useActivityBuilderContext } from 'approot/activity-builder/activity-builder.context';
import { ColourTokenRadio } from 'approot/shared/colour/colour-token';
import SettingTextAligment from '../shared/settings-text-alignment';
import { RangeFieldSetting } from '../shared/range-field';
import { CheckableSingle } from 'approot/shared/styles/buttons/checkable-input';
import { ScreenReaderOnly } from 'approot/shared/styles/global.styles';
import {
  COLOUR_BLUE,
  COLOUR_GREY,
  COLOUR_PURPLE,
} from 'approot/shared/colour/colour.constants';
import ActivityBuilderInlineSvg from 'approot/shared/inline-svg/activity-builder/activity-builder-inline-svg';
import type { GetActivitiesSectionsQuery } from '../../lesson-builder.graphql.generated';
import { grey6 } from 'approot/shared/styles/colours.style';
import type { Pluck } from 'lib/gql/pluck';

const MAX_WIDTH = 100;
export const WIDTH_FIELD = 'width';

type FormData = {
  [WIDTH_FIELD]: number;
};

// there shouldn't be any cases in production where the style object is null
// because when a component is added it gets these values via server/templates/activity-builder-component-templates.ts
// however now that the style object is nullable, we need some fallbacks just in case which have been copied over from the template file
const defaultStyleProperties: Pluck<
  Extract<
    Pluck<GetActivitiesSectionsQuery, 'getActivitiesSections.components'>,
    { __typename: 'ActivitiesInfoBoxComponent' }
  >,
  'properties.style'
> = {
  __typename: 'ComponentStyle',
  arrowAlignment: null,
  arrowPosition: null,
  baseColour: grey6,
  border: BORDER_STYLE_BOX,
  backgroundColour: true,
  width: 100,
  textAlignment: FLEX_ALIGN_START,
  outerBorder: null,
  outerBaseColour: null,
  outerBackgroundColour: null,
};

const InfoBoxSettings = ({ sectionId, id, properties }: SettingsProps) => {
  const formMethods = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      [WIDTH_FIELD]:
        properties.style?.width === null ? MAX_WIDTH : properties.style?.width,
    },
  });
  const { watch } = formMethods;
  const widthValue = watch(WIDTH_FIELD);

  const { closeDrawer } = useActivityBuilderContext();
  const { updateComponentProperties } = useBatchComponentUpdateContext();

  const handleChange = useCallback(
    (newProperties: InfoBoxProperties) => {
      const updatedProperties = {
        ...properties,
        style:
          properties.style || newProperties.style
            ? {
                ...defaultStyleProperties,
                ...(properties.style || {}),
                ...(newProperties.style || {}),
              }
            : null,
      };
      updateComponentProperties({
        input: {
          sectionId,
          componentId: id,
          componentData: {
            properties: updatedProperties,
          },
        },
        properties: updatedProperties,
        typename: INFO_BOX_TYPENAME,
      });
    },
    [id, sectionId, properties, updateComponentProperties]
  );

  const title = useMemo(() => {
    switch (properties.style?.border) {
      case BORDER_STYLE_STATEMENT:
        return 'Statement Box';
      case BORDER_STYLE_INSTRUCTION:
        return 'Instruction Box';
      case BORDER_STYLE_BOX:
        return 'Coloured Box';
      case BORDER_STYLE_NONE:
        return 'Plain Text';
      case BORDER_STYLE_SPEECH:
        return 'Speech Bubble';
      default:
        return 'Plain Text';
    }
  }, [properties.style?.border]);

  const currentPosition = properties.style?.arrowPosition;
  const currentAlignment = properties.style?.arrowAlignment;

  return (
    <ActivityBuilderDrawerWrapper
      captureKeypresses
      onCloseByEscape={closeDrawer}
    >
      <ActivityBuilderDrawerHeader title={title} onClose={closeDrawer} />
      <ActivityBuilderDrawerContent>
        <SettingsGroup as="fieldset">
          <ScreenReaderOnly as="legend">{title} settings</ScreenReaderOnly>
          <FormProvider {...formMethods}>
            <SettingsGroup>
              <SettingsLabel htmlFor="textType">Style</SettingsLabel>
              <CheckableSingle
                name="textType"
                selected={{
                  border: properties.style?.border || BORDER_STYLE_NONE,
                  baseColour: properties.style?.baseColour || null,
                }}
                options={[
                  {
                    label: 'Plain Text',
                    value: {
                      border: BORDER_STYLE_NONE,
                      baseColour: null,
                    },
                    'data-selector': 'plain-text',
                  },
                  {
                    label: 'Statement Box',
                    value: {
                      border: BORDER_STYLE_STATEMENT,
                      baseColour: COLOUR_PURPLE.base,
                    },
                    'data-selector': 'statement-box',
                  },
                  {
                    label: 'Instruction Box',
                    value: {
                      border: BORDER_STYLE_INSTRUCTION,
                      baseColour: COLOUR_BLUE.base,
                    },
                    'data-selector': 'instruction-box',
                  },
                  {
                    label: 'Coloured Box',
                    value: {
                      border: BORDER_STYLE_BOX,
                      baseColour: properties.style?.baseColour,
                    },
                    'data-selector': 'coloured-box',
                  },
                  {
                    label: 'Speech Bubble',
                    value: {
                      border: BORDER_STYLE_SPEECH,
                      baseColour: COLOUR_GREY.base,
                    },
                    'data-selector': 'speech-bubble',
                  },
                ]}
                onSelect={value =>
                  handleChange({
                    ...properties,
                    style: {
                      ...(properties.style || defaultStyleProperties),
                      border: value.border as BorderStyle,
                      baseColour: value.baseColour || null,
                      arrowAlignment:
                        value.border === BORDER_STYLE_SPEECH
                          ? ARROW_ALIGNMENT_CENTRE
                          : null,
                      arrowPosition:
                        value.border === BORDER_STYLE_SPEECH
                          ? POSITION_BOTTOM
                          : null,
                    },
                  })
                }
              />
            </SettingsGroup>

            <RangeFieldSetting
              fieldName={WIDTH_FIELD}
              label="Width"
              max={MAX_WIDTH}
              min={0}
              postfix="%"
              value={widthValue}
              onChange={width =>
                handleChange({
                  ...properties,
                  style: {
                    ...(properties.style || defaultStyleProperties),
                    width,
                  },
                })
              }
            />
          </FormProvider>

          <SettingTextAligment
            alignment={properties.style?.textAlignment ?? FLEX_ALIGN_START}
            setAlignment={textAlignment =>
              handleChange({
                ...properties,
                style: {
                  ...(properties.style || defaultStyleProperties),
                  textAlignment,
                },
              })
            }
          />

          {properties.style?.border === BORDER_STYLE_SPEECH && (
            <>
              <SettingsGroup marginBottom={12}>
                <SettingsLabel>Tail Position</SettingsLabel>
                <CheckableSingle
                  variant="button"
                  isRound
                  name="arrowPosition"
                  options={[
                    {
                      label: (
                        <ActivityBuilderInlineSvg
                          id="icon_info_box_speech_bottom"
                          width={26}
                          height={26}
                        />
                      ),
                      value: POSITION_BOTTOM,
                      'aria-label':
                        'Set speech style to have a bottom arrow position',
                    },
                    {
                      label: (
                        <ActivityBuilderInlineSvg
                          id="icon_info_box_speech_top"
                          width={26}
                          height={26}
                        />
                      ),
                      value: POSITION_TOP,
                      'aria-label':
                        'Set speech style to have a top arrow position',
                    },
                    {
                      label: (
                        <ActivityBuilderInlineSvg
                          id="icon_info_box_speech_right"
                          width={26}
                          height={26}
                        />
                      ),
                      value: POSITION_RIGHT,
                      'aria-label':
                        'Set speech style to have a right arrow position',
                    },
                    {
                      label: (
                        <ActivityBuilderInlineSvg
                          id="icon_info_box_speech_left"
                          width={26}
                          height={26}
                        />
                      ),
                      value: POSITION_LEFT,
                      'aria-label':
                        'Set speech style to have a left arrow position',
                    },
                  ]}
                  selected={currentPosition || POSITION_TOP}
                  onSelect={selected => {
                    let alignment = currentAlignment;
                    if (
                      (selected === ARROW_ALIGNMENT_TOP ||
                        selected === ARROW_ALIGNMENT_BOTTOM) &&
                      (currentAlignment === ARROW_ALIGNMENT_TOP ||
                        currentAlignment === ARROW_ALIGNMENT_BOTTOM)
                    ) {
                      alignment = ARROW_ALIGNMENT_CENTRE;
                    }

                    if (
                      ((selected === ARROW_ALIGNMENT_LEFT ||
                        selected === ARROW_ALIGNMENT_RIGHT) &&
                        currentAlignment === ARROW_ALIGNMENT_LEFT) ||
                      currentAlignment === ARROW_ALIGNMENT_RIGHT
                    ) {
                      alignment = ARROW_ALIGNMENT_CENTRE;
                    }

                    handleChange({
                      ...properties,
                      style: {
                        ...(properties.style || defaultStyleProperties),
                        arrowAlignment: alignment || null,
                        arrowPosition: selected,
                      },
                    });
                  }}
                />
              </SettingsGroup>

              <SettingsGroup>
                <ScreenReaderOnly>
                  Choose an arrow position for the speech bubble style
                </ScreenReaderOnly>
                <CheckableSingle
                  name="arrowAlignment"
                  options={[
                    ...(currentPosition === POSITION_LEFT ||
                    currentPosition === POSITION_RIGHT
                      ? [
                          {
                            label: 'Top',
                            value: ARROW_ALIGNMENT_TOP,
                            'aria-label': 'Set speech arrow position to top',
                          },
                        ]
                      : [
                          {
                            label: 'Left',
                            value: ARROW_ALIGNMENT_LEFT,
                            'aria-label': 'Set speech arrow position to left',
                          },
                        ]),
                    {
                      label: 'Centred',
                      value: ARROW_ALIGNMENT_CENTRE,
                    },
                    ...(currentPosition === POSITION_LEFT ||
                    currentPosition === POSITION_RIGHT
                      ? [
                          {
                            label: 'Bottom',
                            value: ARROW_ALIGNMENT_BOTTOM,
                            'aria-label': 'Set speech arrow position to bottom',
                          },
                        ]
                      : [
                          {
                            label: 'Right',
                            value: ARROW_ALIGNMENT_RIGHT,
                            'aria-label': 'Set speech arrow position to right',
                          },
                        ]),
                  ]}
                  selected={currentAlignment || ARROW_ALIGNMENT_RIGHT}
                  onSelect={value => {
                    handleChange({
                      ...properties,
                      style: {
                        ...(properties.style || defaultStyleProperties),
                        arrowAlignment: value as ArrowAlignment,
                      },
                    });
                  }}
                />
              </SettingsGroup>
            </>
          )}

          {properties.style?.border === BORDER_STYLE_BOX && (
            <SettingsGroup>
              <SettingsLabel aria-label="Choose a background colour">
                Colours
              </SettingsLabel>
              <div role="radiogroup">
                {INFO_BOX_COLOURS.map(item => (
                  <ColourTokenRadio
                    key={item.colour1}
                    name="colourField"
                    colours={item}
                    isActive={item.base === properties.style?.baseColour}
                    onChange={() =>
                      handleChange({
                        ...properties,
                        style: {
                          ...(properties.style || defaultStyleProperties),
                          baseColour: item.base,
                        },
                      })
                    }
                  />
                ))}
              </div>
            </SettingsGroup>
          )}
        </SettingsGroup>
      </ActivityBuilderDrawerContent>
    </ActivityBuilderDrawerWrapper>
  );
};

export default InfoBoxSettings;
